import React, { useEffect, useState } from 'react';
import { ScrollView } from 'react-native';
import { shape } from 'prop-types';
import { ActivityIndicator, Background, Hr, NoDataText } from 'LocalGluons';
import { prependZero, formatDay } from 'Utils';
import { useStore } from 'Store';
import { logEvent } from 'AppManager';
import { Device } from 'DeviceUtils';
import { DETAIL_HEADER_HEIGHT, TOP_BAR_TOTAL_HEIGHT } from '../../Navigation/Menu/Menu.styles';
import { styles } from './Showtimes.jss';
import { Showtime } from './components';
import { useAppErrorHandler } from '../../App/AppErrorHandlerContext/AppErrorHandlerContext';

/**
 * @typedef { import('@24i/st-api-connector').ProgramModel} ProgramModel
 * @typedef {import('react').Dispatch<import('react').SetStateAction<ProgramModel[]>>} SetShowtimes
 */

/**
 * @param {number} ts
 */
function formatDate(ts) {
    const date = new Date(ts);
    const dayName = formatDay(date.getDay(), 2);
    const day = prependZero(date.getDate());
    const month = prependZero(date.getMonth() + 1);

    return `${dayName}., ${day}.${month}`;
}

/**
 * @typedef { import('react-navigation').NavigationScreenProp } Navigation
 * @param {{ navigation: Navigation }} param0
 */
export const Showtimes = ({ navigation, route }) => {
    const seriesName = route.params?.seriesName || undefined;
    const channelId = route.params?.channelId || undefined;
    const endTime = route.params?.endTime || undefined;
    const programId = route.params?.programId || undefined;
    const { appManager } = useStore();
    const { setError } = useAppErrorHandler();
    /**
     * @type {[ProgramModel[], SetShowtimes]} state
     */
    const [showtimes, setShowtimes] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchShowtimes = async () => {
            setLoading(true);
            try {
                const fetchedShowtimes = await appManager.getApi().fetchShowTimes({
                    title: seriesName,
                    channelId,
                    endTime
                });
                setShowtimes(fetchedShowtimes);
            } catch (error) {
                setError({ error, screenId: 'showtimes' });
            } finally {
                setLoading(false);
            }
        };

        fetchShowtimes();
    }, [seriesName, channelId]);

    useEffect(() => {
        logEvent({ type: 'action', subtype: 'showtimesOpen', content1: programId });
        return () => {
            logEvent({ type: 'action', subtype: 'showtimesClose', content1: programId });
        };
    }, []);

    const showtimesExists = showtimes && showtimes.length > 0;
    return (
        <Background
            style={{
                paddingTop: Device.isWeb ? TOP_BAR_TOTAL_HEIGHT + DETAIL_HEADER_HEIGHT : null
            }}
        >
            {!loading && !showtimesExists && <NoDataText />}
            {loading ? (
                <ActivityIndicator />
            ) : (
                <ScrollView style={styles.container}>
                    {showtimesExists && <Hr style={styles.itemDivider} />}
                    {showtimesExists &&
                        showtimes.map((showtime, index) => {
                            const meta = showtime.getMetaData();
                            const date = formatDate(showtime.getStartTime());
                            const time = meta.getAirTimeString();
                            const text = date && time && `${date} | ${time}`;
                            return (
                                <Showtime
                                    index={index}
                                    text={text}
                                    channelName={showtime.data.channelName}
                                    key={showtime.getId()}
                                    onPress={() => {
                                        navigation.navigate('Details', {
                                            id: showtime.data.id,
                                            type: showtime.modelType
                                        });
                                    }}
                                />
                            );
                        })}
                </ScrollView>
            )}
        </Background>
    );
};

Showtimes.propTypes = {
    navigation: shape({}).isRequired
};
